import React, { useEffect, useRef, useState } from "react";
import Login from "../Login/Login";
import "../PostForm.css";
import "./AktualnosciManager.css";

const API_BASE = "https://ekselowe.pl/api";

// Formularz pól posta - używany zarówno do dodawania, jak i edycji wpisu.
const PostFieldsForm = ({ initial, submitLabel, isLoggedin, busy, onSubmit, onCancel }) => {
  const [title, setTitle] = useState(initial.title || "");
  const [text, setText] = useState(initial.text || "");
  const [imageUrl, setImageUrl] = useState(initial.image || "");
  const [uploadedImage, setUploadedImage] = useState(initial.image || "");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedExtension = [".jpg", ".jpeg", ".png"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (allowedExtension.includes("." + ext)) {
      setSelectedFile(file);
      setValidationError(null);
    } else {
      setSelectedFile(null);
      setValidationError("Złe rozszerzenie. Należy użyć .jpg albo .png.");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setValidationError("Najpierw wybierz plik.");
      return;
    }
    setUploading(true);
    try {
      const imageData = new FormData();
      imageData.append("file", selectedFile);
      imageData.append("token", isLoggedin);
      const response = await fetch(`${API_BASE}/imageupload.php`, {
        method: "POST",
        body: imageData,
      });
      const responseData = await response.json();
      setUploadedImage("/public/uploads/" + responseData.image_link);
      setImageUrl("");
    } catch (err) {
      setValidationError("Nie udało się wrzucić pliku na serwer.");
    } finally {
      setUploading(false);
    }
  };

  const resetImage = () => {
    setUploadedImage("");
    setSelectedFile(null);
    setValidationError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !text) return;
    onSubmit({
      title,
      text,
      image: imageUrl,
      file: uploadedImage,
    });
  };

  const previewImage = uploadedImage || imageUrl;

  return (
    <form className="am-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <input
          type="text"
          placeholder="Tytuł Posta"
          required
          className="form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="form-row">
        {!uploadedImage && (
          <input
            placeholder="URL obrazka (albo wrzuć plik obok)"
            className="form-input"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        )}
        {!uploadedImage && (
          <div className="col col-3">
            <input type="file" ref={fileInputRef} accept=".jpg,.jpeg,.png" onChange={handleFileChange} />
            <button className="btn btn-primary" type="button" onClick={handleUpload} disabled={uploading}>
              {validationError ? validationError : uploading ? "Wysyłanie…" : "Wrzuć na serwer"}
            </button>
          </div>
        )}
      </div>
      {previewImage && (
        <div className="am-image-preview-row">
          <img src={previewImage} alt="" className="post-form-preview-image" />
          <button className="btn" type="button" onClick={resetImage}>Zmień obrazek</button>
        </div>
      )}
      <textarea
        placeholder="Treść posta"
        required
        className="form-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="am-form-actions">
        <button type="submit" className="submit-button" disabled={busy}>
          {busy ? "Zapisywanie…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>Anuluj</button>
        )}
      </div>
    </form>
  );
};

const AktualnosciManager = ({ isLoggedin, setIsLoggedin }) => {
  const [posts, setPosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchPosts = () => {
    setLoading(true);
    fetch(`${API_BASE}/getposts.php`, {
      headers: { "Content-Type": "application/json; charset=UTF-8" },
    })
      .then((res) => res.json())
      .then((json) => setPosts(json || {}))
      .catch(() => setPosts({}))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isLoggedin) fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedin]);

  if (!isLoggedin) {
    return <Login setToken={setIsLoggedin} />;
  }

  const submitToApi = async (url, fields) => {
    setBusy(true);
    try {
      const body = new URLSearchParams({
        title: fields.title,
        text: fields.text,
        image: fields.image || "",
        file: fields.file || "",
        token: isLoggedin,
        ...(fields.id !== undefined ? { id: fields.id } : {}),
      });
      await fetch(url, { method: "POST", body });
      fetchPosts();
      setShowAddForm(false);
      setEditingKey(null);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = (fields) => submitToApi(`${API_BASE}/addpost.php`, fields);
  const handleEditSubmit = (key, fields) => submitToApi(`${API_BASE}/editpost.php`, { ...fields, id: key });

  const handleDelete = async (key, title) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć post "${title}"?`)) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE}/deletepost.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idd: key, token: isLoggedin }),
      });
      fetchPosts();
    } finally {
      setBusy(false);
    }
  };

  const keys = Object.keys(posts).reverse();

  return (
    <div className="am-wrapper">
      <h1 className="am-title">Zarządzaj Aktualnościami</h1>
      <p className="am-subtitle">Tutaj dodajesz, edytujesz i usuwasz wpisy widoczne na stronie „Aktualności”.</p>

      {!showAddForm && (
        <button className="submit-button am-add-toggle" onClick={() => { setShowAddForm(true); setEditingKey(null); }}>
          + Dodaj nowy wpis
        </button>
      )}

      {showAddForm && (
        <div className="am-card am-add-card">
          <h2>Nowy wpis</h2>
          <PostFieldsForm
            initial={{ title: "", text: "", image: "" }}
            submitLabel="Dodaj wpis"
            isLoggedin={isLoggedin}
            busy={busy}
            onSubmit={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {loading && <p>Wczytywanie wpisów…</p>}

      <div className="am-list">
        {!loading && keys.length === 0 && <p>Nie masz jeszcze żadnych wpisów.</p>}
        {keys.map((key) => {
          const post = posts[key];
          const isEditing = editingKey === key;
          return (
            <div className="am-card" key={key}>
              {isEditing ? (
                <>
                  <h2>Edytuj wpis</h2>
                  <PostFieldsForm
                    initial={post}
                    submitLabel="Zapisz zmiany"
                    isLoggedin={isLoggedin}
                    busy={busy}
                    onSubmit={(fields) => handleEditSubmit(key, fields)}
                    onCancel={() => setEditingKey(null)}
                  />
                </>
              ) : (
                <div className="am-row">
                  {post.image && <img src={post.image} alt={post.title} className="am-thumb" />}
                  <div className="am-row-text">
                    <h3>{post.title}</h3>
                    <p>{post.text}</p>
                  </div>
                  <div className="am-row-actions">
                    <button className="btn" onClick={() => { setEditingKey(key); setShowAddForm(false); }}>Edytuj</button>
                    <button className="btn am-delete" onClick={() => handleDelete(key, post.title)}>Usuń</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AktualnosciManager;
